import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { Info, X, CheckCircle, Loader2 } from 'lucide-react';
import {
  PaymentPlanDto,
  paymentPlansApi,
  handleApiError
} from '../../services/paymentsService';

interface UpdateCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentPlan: PaymentPlanDto;
  onSuccess: () => void;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  padding: 24px;
  max-width: 640px;
  width: 100%;
  max-height: 95vh;
  overflow-y: auto;
  box-shadow: var(--shadow-dark);
  direction: rtl;

  @media (max-width: 767px) {
    padding: 16px;
    max-width: 100%;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  border-radius: var(--border-radius-md);
  transition: background 0.15s ease;

  &:hover {
    background: var(--background-secondary);
  }
`;

const InfoBanner = styled.div`
  background: rgba(0, 115, 234, 0.08);
  border: 1px solid rgba(0, 115, 234, 0.25);
  border-radius: var(--border-radius-md);
  padding: 12px 14px;
  margin-bottom: 16px;
  color: #0c4a85;
  font-size: 14px;
  line-height: 1.5;
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const InfoIconWrap = styled.div`
  flex-shrink: 0;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  padding-top: 1px;
`;

const IframeWrap = styled.div`
  width: 100%;
  height: 600px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }
`;

const LoadingPanel = styled.div`
  width: 100%;
  height: 600px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: var(--text-secondary);
  font-size: 14px;
  background: var(--background-secondary);
`;

const SuccessPanel = styled.div`
  width: 100%;
  height: 600px;
  border: 1px solid var(--success-color, #16a34a);
  border-radius: var(--border-radius-md);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: rgba(22, 163, 74, 0.05);
`;

const SuccessText = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #16a34a;
`;

const StatusFooter = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-secondary);
  justify-content: center;
`;

const Spin = styled.span`
  display: inline-flex;
  animation: spin 1s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

type View = 'loading' | 'iframe' | 'success';

const UpdateCardModal: React.FC<UpdateCardModalProps> = ({
  isOpen,
  onClose,
  paymentPlan,
  onSuccess
}) => {
  const [view, setView] = useState<View>('loading');
  const [iframeUrl, setIframeUrl] = useState<string>('');
  const initialTokenRef = useRef<string | undefined>(paymentPlan.cardcomToken);
  const pollingRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    initialTokenRef.current = paymentPlan.cardcomToken;
    setView('loading');

    let cancelled = false;

    const loadUrl = async () => {
      try {
        const resp = await paymentPlansApi.getCardUpdateUrl(paymentPlan.id);
        if (cancelled) return;
        setIframeUrl(resp.iframeUrl);
        setView('iframe');
        startPolling();
      } catch (error) {
        if (cancelled) return;
        toast.error(handleApiError(error));
        onClose();
      }
    };

    loadUrl();

    return () => {
      cancelled = true;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, paymentPlan.id]);

  const stopPolling = () => {
    if (pollingRef.current) {
      window.clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = () => {
    stopPolling();
    pollingRef.current = window.setInterval(async () => {
      try {
        const plan = await paymentPlansApi.getById(paymentPlan.id);
        if (plan.cardcomToken && plan.cardcomToken !== initialTokenRef.current) {
          stopPolling();
          setView('success');
          window.setTimeout(() => {
            onSuccess();
            onClose();
          }, 1500);
        }
      } catch {
        // Ignore polling errors - retry on next tick
      }
    }, 3000);
  };

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <HeaderRow>
          <Title>עדכון פרטי כרטיס אשראי</Title>
          <CloseButton onClick={onClose} aria-label="סגור">
            <X size={20} />
          </CloseButton>
        </HeaderRow>

        <InfoBanner>
          <InfoIconWrap>
            <Info size={18} />
          </InfoIconWrap>
          <div>יש להציג את המסך ללקוח למילוי פרטי הכרטיס החדש.</div>
        </InfoBanner>

        {view === 'loading' && (
          <LoadingPanel>
            <Spin><Loader2 size={32} /></Spin>
            <div>טוען טופס סליקה...</div>
          </LoadingPanel>
        )}

        {view === 'iframe' && iframeUrl && (
          <>
            <IframeWrap>
              <iframe src={iframeUrl} title="Cardcom card update" allow="payment" />
            </IframeWrap>
            <StatusFooter>
              <Spin><Loader2 size={14} /></Spin>
              ממתין לעדכון פרטי כרטיס...
            </StatusFooter>
          </>
        )}

        {view === 'success' && (
          <SuccessPanel>
            <CheckCircle size={64} color="#16a34a" />
            <SuccessText>פרטי הכרטיס עודכנו בהצלחה</SuccessText>
          </SuccessPanel>
        )}
      </Modal>
    </Overlay>
  );
};

export default UpdateCardModal;
